import { PageProps, KiNoPost } from '../../components/types'
import HeaderDetail from '../../components/HeaderDetail'
import { 
  BasePicklistItemColumn,
  PicklistItemWithDrawColumn,
  PicklistItemCheckMvColumn
} from '../../constants/Column'
import Comment from '../../components/Comment'
import { syncPicklistOrder } from '../../apis'
import Modal from '../../components/Modal'
import { groupBy as rowGrouper } from 'lodash';

import OrderPicklistReducer, { 
  INITIAL_STATE, 
  PicklistAction
} from '../../states/PicklistReducer'
import { 
  postComment, 
  getRemotePicklistById,
  whApprovePicklist,
  getMovementsById,
  whCancelPicklist,
  whSaveWithShortagePicklist,
  extraPicklistsByOrderId,
  updateStatusComment
} from '../../states/PicklistAction'

import DataGrid from 'react-data-grid'
import { useReducer, useEffect, useCallback, useState, useMemo } from 'react'

import { 
  AiOutlineCloudSync, 
  AiOutlineCheckCircle, 
  AiOutlineQuestionCircle,
  AiOutlineArrowRight
} from 'react-icons/ai'

import { 
  ImCancelCircle
} from 'react-icons/im'

import { 
  IoMdCodeWorking
} from 'react-icons/io'

import {
  TbTruckDelivery
} from 'react-icons/tb'

import {
  FaWarehouse
} from 'react-icons/fa'

import { useHistory, Link } from 'react-router-dom'

import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { DgtPdOrderPicklist, DgtPdOrderPicklistExtraReturn } from '../../models'
import { formatDateFromUnix } from '../../utilities/Format'
import Badge from '../../components/Badge'
import { PicklistStatus } from '../../constants/EnumType'

const Loading = () => <div className="w-4 h-4 border-2 border-white border-solid rounded-full animate-spin border-t-transparent" />

const GoodIssueOrderDetail = ({ title, id, username, readOnly, orderType } : PageProps) => {
  const [ state, dispatch ] = useReducer(
    OrderPicklistReducer, INITIAL_STATE);
  
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  );

  const [ showDialogCancel, setShowCancel ] = useState<boolean>(false)
  const [ showDialogShortage, setShowShortage ] = useState<boolean>(false)
  const [ extras, setExtras ] = useState<DgtPdOrderPicklistExtraReturn[]>([])

  const history = useHistory()

  const formMethods = useForm<KiNoPost>({
    defaultValues: {
      kiNo: ''
    },
    resolver: yupResolver(yup.object({
      kiNo: yup.string(),
    }))
  })

  useEffect(() => {
    const fetchPicklist = async (id: string) => {
      const { header, items, comments } = await getRemotePicklistById(id!)
      dispatch({ type: PicklistAction.Inited, payload: { header, items, comments } })
      formMethods.setValue('kiNo', header.kiNo || '')
    }
    fetchPicklist(id!)

    const fetchMovements = async (orderId: string) => {
      const mvs = await getMovementsById(orderId)
      dispatch({ 
        type: PicklistAction.PicklistItemMovementsUpdated, 
        payload: { 
          itemMovements: mvs.filter(
            itm => itm.movementType !== '262' && 
            itm.materialType !== 'BULK' &&
            itm.materialDescription !== 'DIW' &&
            !itm.extraRefId)
        }})
    }
    fetchMovements(id!)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id ])

  useEffect(() => {
    const fetchExtras =async (orderId: string) => {
      const extras = await extraPicklistsByOrderId(orderId)
      setExtras(extras)
    }
    fetchExtras(id!)
  }, [ id ])

  const openDialog = () => {
    dispatch({ type: PicklistAction.OpenDialog })
  }

  const onPostComment = useCallback(async (comment: string) => {
    const commentResponse = await postComment(
      state.header?.pk!,  
      comment, 
      username!
    )
    dispatch({ type: PicklistAction.PostComment, payload : { comments: [commentResponse] } })
  }, [ state.header?.pk, username ])

  const onActiveComment = useCallback(async (orderId: string, skCommentId: string, active: boolean, version: number) => {
		const updatedComment = await updateStatusComment(orderId, skCommentId, active, version)
    dispatch({ type: PicklistAction.UpdateComment, payload: { comments: [updatedComment] } })
  }, [])

  const clickSyncSap = async () => {
    dispatch({ type: PicklistAction.StartSync })
    // sync from sap
    const result = await syncPicklistOrder(
      state.header?.pk!,
      state.header?.division!, 
      state.items.map(itm => {
        return {
          materialCode: itm.materialCode,
          materialDescription: itm.materialDescription,
          materialType: itm.materialType,
          materialRequestQty: itm.requestQty
        }
      }),
      state.itemMovements.map(mv => mv.sk)
    );
    const mvs = await getMovementsById(state.header?.pk!)
    dispatch({
      type: PicklistAction.SyncCompleted, 
      payload: { 
        errorSync : result.success ? '' : result.message,
        itemMovements: mvs.filter(itm => 
          itm.movementType !== '262' && 
          itm.materialType !== 'BULK' &&
          itm.materialDescription !== 'DIW' &&
          !itm.extraRefId)
      }
    })
  }

  const onApproved = async () => {
    const kiNo = formMethods.getValues('kiNo')
    dispatch({ type: PicklistAction.PicklistUpdating })
    const updated = await whApprovePicklist(state.header!, state.items, state.itemMovements, username!, kiNo)
    dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: updated } })
    history.goBack()
  }

  const onSaveForShortage = async () => {
    dispatch({ type: PicklistAction.PicklistUpdating })
    const updated = await whSaveWithShortagePicklist(state.header!, state.items, state.itemMovements, username!)
    dispatch({ type: PicklistAction.PicklistUpdated, payload: { header: updated } })
    history.goBack()
  }

  const onCancel = async () => {
    await whCancelPicklist(state.header!)
    history.goBack()
  }
  
  const closeDialog = () => {
    dispatch({ type: PicklistAction.CloseDialog })
  }

  const { header, items, isSyncing, comments, 
    errorMessage, showConfirmDialog, itemMovements, isSubmitting } = state

  const isBlockApprove = itemMovements.length === 0
  
  const withDrawItems = new Set(itemMovements.map(itm => itm.materialCode))

  useEffect(() => {
    if (itemMovements.length > 0) {
      setExpandedGroupIds(new Set(itemMovements.map(itm => itm.materialCode)))
    }
  }, [ itemMovements ])

  const onSubmit: SubmitHandler<KiNoPost> = async (data) => {
    openDialog()
  }

  const isNotReadyToApprove = header?.status === PicklistStatus.PD_RELEASED

  const mapBarcodeMovements = useMemo(() => {
    return itemMovements.map(mv => {
      const barcodeItem = items.find(itm => itm.materialCode === mv.materialCode)?.barcodePkg
      if (barcodeItem) return { ...mv, barcode: barcodeItem }
      return mv
    })
  }
  , [ itemMovements, items ])

  return <div className="page-container">
    <FormProvider {...formMethods} >
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
      <HeaderDetail title={`${title} Order : ${id}`} header={header}
        isShowRelease={false} isInputKi={true}
        readOnly={readOnly}
        isPdNotApprove={isNotReadyToApprove}/>
      {readOnly || isNotReadyToApprove ? null : <div className='flex items-end justify-between mt-5 mb-2 md:mt-1'>
        <div className='flex items-center gap-2'>
          <button type='button' className='gap-2 btn-sap' onClick={clickSyncSap} disabled={isSyncing}>
            {isSyncing ? <><Loading /> กำลังเชื่อมต่อ SAP</> : <><AiOutlineCloudSync size={24} /> กดเพื่อดึงยอดจ่ายจาก SAP</>}
          </button>
          <small className='text-red-500 '>{errorMessage}</small>
        </div>
        <div className='flex items-center gap-2'>
          <button className='gap-1 btn-outline-danger'
            onClick={() => setShowCancel(true)}
            type='button'>
              <ImCancelCircle />ยกเลิก
          </button>
          <button className='gap-1 btn-outline-primary'
            onClick={() => setShowShortage(true)}
            disabled={isBlockApprove}
            type='button' >
              <IoMdCodeWorking />บันทึก รอของเข้า
          </button>
          <button className='gap-1 btn-success w-[150px]'
            disabled={isBlockApprove}
            type='submit'>
              <AiOutlineCheckCircle />อนุมัติจบ
          </button>
        </div>
      </div>
      }
      </form>
    </FormProvider>
    <small>จ่าย {withDrawItems.size} จากทั้งหมด {items.length} รายการ</small>
    {mapBarcodeMovements.length === 0 ?
    <DataGrid 
      className='mb-3 '
      style={{ height: "100%", contain: "none" }}
      columns={[...BasePicklistItemColumn, ...PicklistItemWithDrawColumn]}
      rows={items}
      defaultColumnOptions={{ resizable: true }}
      rowClass={row => row.deprecated ? 'deprecated-item-row':''}
      rowHeight={(arg) => {
        const itm = arg.row as DgtPdOrderPicklist
        if (itm.barcodePkg) return 55
        return 35
      }}
    />
    :
    <>
      <DataGrid
        className='mb-3 '
        style={{ height: "100%", contain: "none" }}
        columns={PicklistItemCheckMvColumn.slice(0, -2)} // remove SelectColumn/CheckTime
        rows={mapBarcodeMovements}
        rowKeyGetter={(row) => row.id}
        groupBy={['materialCode']}
        rowGrouper={rowGrouper}
        expandedGroupIds={expandedGroupIds}
        onExpandedGroupIdsChange={setExpandedGroupIds}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={(arg) => {
          if (arg.type === 'GROUP' && arg.row.childRows[0].barcode) return 55
          return 35
        }}
      />
      <small className={`${items.length - withDrawItems.size === 0 ? 'hidden':''}`}>
        รายการที่ไม่มีจ่าย
      </small>
      <DataGrid 
        className={` mb-3 ${items.length - withDrawItems.size === 0 ? 'hidden':''}`}
        style={{ height: "100%", contain: "none" }}
        columns={[...BasePicklistItemColumn, ...PicklistItemWithDrawColumn].slice(0, -1)}
        rows={items.filter(
          itm => !Array.from(withDrawItems).includes(itm.materialCode)
        )}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={(arg) => {
          const itm = arg.row as DgtPdOrderPicklist
          if (itm.barcodePkg) return 55
          return 35
        }}
      />
    </>
    }
    {extras.length > 0 ? 
    <div className='mb-3 '>
      <small>รายการเบิกเพิ่ม {extras.length} รายการ</small>
      {extras.map((extra, idx) => 
        <div key={extra.id} className='flex items-center gap-1 italic'>
          <h6>{idx+1}. {extra.remark} ขอโดย {extra.requestedBy} / {formatDateFromUnix(extra.requestedDateTime)}
          {extra.withdrawedDateTime ? <Badge variant='success mx-2'>อนุมัติจ่ายแล้ว</Badge>:''}
          {extra.canceledDateTime ? <Badge variant='bg-gray-900 mx-2 text-white text-sm font-light'>{`ยกเลิกแล้ว ${formatDateFromUnix(extra.canceledDateTime)}`}</Badge>:''}
          </h6>
          <Link to={`/good-issue-order/${orderType}/${id}/extra/${extra.id}`} target="_blank">
            <h6 className='text-blue-600 underline '>กดดูใบเบิกเพิ่ม</h6>
          </Link>
        </div>
      )}
    </div> : null}
    <Comment comments={comments.sort((a, b) => a.sk.localeCompare(b.sk))} onPost={onPostComment} onActive={onActiveComment} username={username}/>
    <Modal show={showConfirmDialog}
      onHide={closeDialog}
      okText="อนุมัติ"
      okTheme='success'
      okAction={onApproved}
      cancelText="ยกเลิก"
      title={`อนุมัติใบเบิก #${id} ?`}
      loading={isSubmitting}
    >
      <div className='flex flex-col items-center gap-2 text-center'>
        <AiOutlineQuestionCircle size={40}/>
        <h5>อนุมัติใบเบิก {id} คุณยืนยันใช่หรือไม่ ?</h5>
        <h6 className=' text-muted'>ระบบจะส่งต่อใบเบิกไปให้ PD เพื่อทำรับสินค้าต่อไป</h6>
      </div>
    </Modal>
    <Modal show={showDialogShortage}
      onHide={() => setShowShortage(false)}
      okText="บันทึก"
      okTheme='primary'
      okAction={onSaveForShortage}
      cancelText="ยกเลิก"
      title={`บันทึกใบเบิก #${id} ?`}
      loading={isSubmitting}
    >
      <div className='flex flex-col items-center gap-2 text-center'>
        <div className='flex items-center justify-center gap-3'>
          <TbTruckDelivery size={40}/>
          <AiOutlineArrowRight size={20}/>
          <FaWarehouse size={36}/>
        </div>
        <h5>บันทึกใบเบิก {id} ไว้ก่อน รอของเข้า (Shortage)</h5>
        <h6 className=' text-muted'>เบื้องต้น ระบบจะส่งต่อใบเบิกไปให้ PD เพื่อทำรับสินค้ารายการที่มีไปก่อน</h6>
      </div>
    </Modal>
    <Modal show={showDialogCancel} onHide={() => setShowCancel(false)}
      okTheme='danger'
      okText='ยืนยัน'
      cancelAction={() => setShowCancel(false)}
      okAction={onCancel}
      cancelText='ปิด'
      title={`คำเตือนเนื่องจากมีการยกเลิกใบเบิก`}
      >
      <div className='flex items-center gap-2'>
        <ImCancelCircle size={50} className='text-red-500 basis-1/6 ' />
        <div className='space-y-2 basis-5/6'>
          <h6>คุณกำลังจะยกเลิกใบเบิก PD ORDER</h6>
          <h5 className='font-bold underline '>{id}</h5>
          <h6>กลับไปที่ Production แน่ใจแล้วหรือไม่ ?</h6>
        </div>
      </div>
    </Modal>
  </div>
}

export default GoodIssueOrderDetail